// Hours spent on an issue, asked for with every resolve (owner, 2026-09-16).
//
// The server refuses a resolve without them (back/utils/issueHours), so
// every place that resolves asks the same way: the resolve modal carries
// HoursSpentField, and the one-click paths (status dropdown, bulk,
// deliver, revision rail) call askHoursSpent() first.
import React, { useState } from "react";
import { InputNumber, Modal, message } from "antd";
import { T } from "pages/Issue/theme";

// NO UPPER LIMIT (owner, 2026-10-19: "remove the spent hours cap too").
// Exported still, because this file's test and the server's issueHours both
// name it; Infinity makes every comparison against it pass and keeps the
// import working.
export const MAX_HOURS = Infinity;

export function validHours(v) {
  const n = Number(v);
  return v !== null && v !== undefined && v !== "" && Number.isFinite(n) && n > 0;
}

export function HoursSpentField({ value, onChange, autoFocus = false, label = "HOURS SPENT", help = "", ariaLabel = "Hours spent" }) {
  const bad = value !== null && value !== undefined && value !== "" && !validHours(value);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>
        {label} <span style={{ color: "#B84B0A" }}>*</span>
      </div>
      <InputNumber
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        min={0.25}
        step={0.25}
        precision={2}
        value={value}
        onChange={onChange}
        placeholder="e.g. 2.5"
        addonAfter="hours"
        status={bad ? "error" : undefined}
        style={{ width: 200 }}
      />
      <div style={{ fontSize: 11.5, color: bad ? "#B84B0A" : T.muted, marginTop: 3 }}>
        {bad ? "Enter the hours spent - any number above 0." : (help || "Time spent on this issue - required to resolve it.")}
      </div>
    </div>
  );
}

function PromptBody({ onValue, help, label, ariaLabel }) {
  const [v, setV] = useState(null);
  return <HoursSpentField autoFocus value={v} onChange={(n) => { setV(n); onValue(n); }} help={help} label={label} ariaLabel={ariaLabel} />;
}

/**
 * Ask for hours before a one-click resolve. Resolves to the number, or
 * null when the person cancels.
 */
export function askHoursSpent({ title = "Hours spent", okText = "Resolve", help = "" } = {}) {
  return new Promise((resolve) => {
    let value = null;
    Modal.confirm({
      title,
      icon: null,
      okText,
      width: 420,
      content: <PromptBody onValue={(n) => { value = n; }} help={help} />,
      onOk: () => {
        if (!validHours(value)) {
          message.error("Enter the hours spent to resolve.");
          return Promise.reject(new Error("hours required"));
        }
        resolve(Number(value));
        return undefined;
      },
      onCancel: () => resolve(null),
    });
  });
}


// ESTIMATED HOURS on a new issue (owner, 2026-10-16) - required wherever a
// person creates one. The forms carry the field; the one-click paths (a 3D
// diff "Make an issue") ask with askEstimatedHours() first.
const ESTIMATE_HELP = "How long this issue should take - required to create it.";

/**
 * Ask for the estimated hours before a one-click create. Resolves to the
 * number, or null when the person cancels (and then nothing is created).
 */
export function askEstimatedHours({ title = "Estimated hours", okText = "Create issue", help = ESTIMATE_HELP } = {}) {
  return new Promise((resolve) => {
    let value = null;
    Modal.confirm({
      title,
      icon: null,
      okText,
      width: 420,
      content: <PromptBody onValue={(n) => { value = n; }} help={help} label="ESTIMATED HOURS" ariaLabel="Estimated hours" />,
      onOk: () => {
        if (!validHours(value)) {
          message.error("Enter the estimated hours to create the issue.");
          return Promise.reject(new Error("estimate required"));
        }
        resolve(Number(value));
        return undefined;
      },
      onCancel: () => resolve(null),
    });
  });
}
